import { useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';

// Weather on the dashboard (CR #98) — 7-day Cowichan Valley forecast from the
// existing /api/weather endpoint (open-meteo, 3h server cache), with the same
// work-risk grading the scheduling side uses. Exports its forecast upward so
// MiniCalendar can tint risky days.

export interface DayForecast {
  date: string;
  t_max: number;
  t_min: number;
  precip_mm: number;
  precip_prob: number;
  snow_cm: number;
  wind_kmh: number;
  code: number;
  risk: 'none' | 'caution' | 'high';
}

// WMO weather codes → material symbol
const iconFor = (code: number): string => {
  if (code === 0) return 'clear_day';
  if (code <= 2) return 'partly_cloudy_day';
  if (code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'foggy';
  if (code >= 51 && code <= 57) return 'rainy_light';
  if (code >= 61 && code <= 67) return 'rainy';
  if (code >= 71 && code <= 77) return 'weather_snowy';
  if (code >= 80 && code <= 82) return 'rainy';
  if (code >= 85 && code <= 86) return 'weather_snowy';
  if (code >= 95) return 'thunderstorm';
  return 'partly_cloudy_day';
};

const riskCls: Record<DayForecast['risk'], string> = {
  none: 'text-gray-700',
  caution: 'text-warning',
  high: 'text-critical',
};

export const WeatherStrip = ({ onForecast }: { onForecast?: (days: DayForecast[]) => void }) => {
  const [days, setDays] = useState<DayForecast[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiClient.get('/weather')
      .then(r => {
        const d = (r.data.data ?? []) as DayForecast[];
        setDays(d);
        onForecast?.(d);
      })
      .catch(() => setFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed || days.length === 0) return null;

  const dayName = (iso: string, i: number) =>
    i === 0 ? 'Today' : new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short' });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
          <span className="material-symbols-outlined text-lg text-primary">partly_cloudy_day</span>
          Site Weather
        </h3>
        <span className="text-[10px] text-gray-400">Duncan / Cowichan Valley</span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.slice(0, 7).map((d, i) => (
          <div key={d.date}
            title={`${d.precip_prob}% precip · ${d.precip_mm}mm${d.snow_cm ? ` · ${d.snow_cm}cm snow` : ''} · wind ${Math.round(d.wind_kmh)}km/h${d.risk !== 'none' ? ` — ${d.risk === 'high' ? 'poor' : 'marginal'} work day` : ''}`}
            className={`flex flex-col items-center gap-0.5 rounded-lg py-1.5 ${d.risk === 'high' ? 'bg-critical/5' : d.risk === 'caution' ? 'bg-warning/5' : ''}`}>
            <span className="text-[10px] font-semibold text-gray-500">{dayName(d.date, i)}</span>
            <span className={`material-symbols-outlined text-xl leading-none ${riskCls[d.risk]}`}>{iconFor(d.code)}</span>
            <span className="text-[11px] font-bold text-gray-800">{d.t_max}°</span>
            <span className="text-[10px] text-gray-400">{d.t_min}°</span>
            {d.precip_prob >= 40 && (
              <span className="text-[9px] font-semibold text-blue-500">{d.precip_prob}%</span>
            )}
          </div>
        ))}
      </div>
      {days.slice(0, 7).some(d => d.risk === 'high') && (
        <p className="mt-2 text-[11px] text-critical flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">warning</span>
          Rough weather ahead — check outdoor scheduling.
        </p>
      )}
    </div>
  );
};
